// src/components/PdfPreviewModal/index.tsx
import type React from 'react';
import { type GridApi } from 'ag-grid-community';
import { ForgeIcon, ForgeDialog, ForgeIconButton } from '@tylertech/forge-react';

import {
  type CustomSortConfig,
  type OrderConfig,
  type LinkedAction,
  type Hierarchy,
} from 'common/visualizations/vif';
import { I18n } from 'common/i18n';
import {
  type TableColumnFormat,
  type ExportData,
  type HeaderFormatStyle,
  type TableExportType,
} from 'common/visualizations/views/agGridReact/types';
import { type ColumnAggregation } from 'common/visualizations/dataProviders/types';
import type { TableActionsConfigs } from 'common/visualizations/views/agGridReact/TableActionsButton/types';
import { type RowStripeStyle } from 'common/types/agGrid/rowStripe';
import { type Dataset, type ReportMetadata } from './types';
import { PreviewPane } from './PreviewPane';
import './PdfPreviewModal.scss';

interface PdfPreviewModalProps {
  dataset: Dataset;
  getExportData?: (
    selectedFiltered: boolean,
    exportType: TableExportType,
    rowLimit?: number,
  ) => Promise<ExportData>;
  gridApi: GridApi | null;
  onClose: () => void;
  reportMetadata: ReportMetadata;
  nonStandardAggregations: ColumnAggregation[] | null;
  columnFormats: { [key: string]: TableColumnFormat };
  vifOrderConfig: OrderConfig[];
  showSubTotal: boolean;
  showTotal: boolean;
  hideDuplicateGroups?: boolean;
  columnSortConfig?: CustomSortConfig;
  tableActionsContext?: TableActionsConfigs;
  linkedAction?: LinkedAction;
  hierarchyConfig?: Hierarchy;
  rowStripeStyle?: RowStripeStyle;
  headerFormat?: HeaderFormatStyle;
  expandedGroupsMap?: Record<string, boolean>;
  agGridOpenNodeLevel?: number;
  isIndented?: boolean;
}

export const PdfPreviewModal: React.FC<PdfPreviewModalProps> = ({
  dataset,
  getExportData,
  gridApi,
  onClose,
  reportMetadata,
  columnFormats,
  nonStandardAggregations,
  vifOrderConfig,
  showSubTotal,
  showTotal,
  hideDuplicateGroups,
  columnSortConfig,
  tableActionsContext,
  linkedAction,
  hierarchyConfig,
  rowStripeStyle,
  headerFormat,
  expandedGroupsMap,
  agGridOpenNodeLevel,
  isIndented,
}) => {
  const dialogProps = { fullscreen: true };

  return (
    <ForgeDialog
      open={true}
      options={dialogProps}
      on-forge-dialog-close={onClose}
      aria-labelledby="pdf-preview-title"
    >
      <div className="pdf-preview-modal">
        <div className="preview-modal-header">
          <h2 id="pdf-preview-title">{I18n.t('common.pdf_preview.print_preview')}</h2>
          <ForgeIconButton slot="end" aria-label="Close Print Preview" onClick={onClose}>
            <ForgeIcon name="close"></ForgeIcon>
          </ForgeIconButton>
        </div>
        <div className="pdf-preview-body">
          {gridApi && getExportData && (
            <PreviewPane
              tableActionsContext={tableActionsContext}
              linkedAction={linkedAction}
              dataset={dataset}
              getExportData={getExportData}
              gridApi={gridApi}
              nonStandardAggregations={nonStandardAggregations}
              columnFormats={columnFormats}
              reportMetadata={reportMetadata}
              showSubTotal={showSubTotal}
              showTotal={showTotal}
              hideDuplicateGroups={hideDuplicateGroups}
              vifOrderConfig={vifOrderConfig}
              columnSortConfig={columnSortConfig}
              hierarchyConfig={hierarchyConfig}
              rowStripeStyle={rowStripeStyle}
              headerFormat={headerFormat}
              expandedGroupsMap={expandedGroupsMap}
              agGridOpenNodeLevel={agGridOpenNodeLevel}
              isIndented={isIndented}
            />
          )}
        </div>
      </div>
    </ForgeDialog>
  );
};
